import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Bell, FlaskConical, Search } from 'lucide-react';
import './pages.css';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { useAuth, authHeaders } from '../auth';
import { LoadingState, EmptyState, ErrorState } from '../components/ui/States';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

const SEVERITY_LABELS = { INFO: 'Information', WARNING: 'Avertissement', CRITICAL: 'Critique' };
// New/updated alerts now arrive live via the shared Socket.IO connection's
// 'alert_update' event (see server.js's emitAlertUpdate). This 30s REST
// poll is kept as a fallback/reconciliation mechanism only - it covers a
// missed event, a reconnect gap, or a backend restart, not the primary
// delivery path anymore.
const REFRESH_INTERVAL_MS = 30000;

export default function AlertsPage() {
  const { token, user } = useAuth();
  const { plants, socket } = useOutletContext();
  const toast = useToast();
  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState(false);
  const [testing, setTesting] = useState(false);

  const [plantFilter, setPlantFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showTest, setShowTest] = useState(false);

  const safePlants = Array.isArray(plants) ? plants : [];
  const plantNameById = useMemo(() => {
    const map = new Map();
    safePlants.forEach((p) => map.set(p.plant_id, p.name || p.plant_id));
    return map;
  }, [safePlants]);
  const plantLabel = (plantId) => (plantId ? (plantNameById.get(plantId) || plantId) : 'Centrale inconnue');

  const load = (cancelledRef) => {
    fetch(`${API_BASE}/alerts?limit=100`, { headers: authHeaders(token) })
      .then((r) => { if (!r.ok) throw new Error('failed'); return r.json(); })
      .then((data) => { if (!cancelledRef?.current) { setAlerts(Array.isArray(data) ? data : []); setError(false); } })
      .catch(() => { if (!cancelledRef?.current) { setAlerts((prev) => prev ?? []); setError(true); } });
  };

  useEffect(() => {
    const cancelledRef = { current: false };
    load(cancelledRef);
    const interval = setInterval(() => load(cancelledRef), REFRESH_INTERVAL_MS);
    return () => { cancelledRef.current = true; clearInterval(interval); };
  }, [token]);

  // Live delivery: merge by id rather than replacing wholesale - a new
  // alert is prepended, an existing one (e.g. ACTIVE -> RESOLVED) is
  // updated in place so it never appears as a duplicate row. Filters/
  // counts/sorting are all derived from `alerts` via useMemo below, so
  // they recompute automatically the moment this state changes.
  useEffect(() => {
    if (!socket) return;
    const onAlertUpdate = (incoming) => {
      setAlerts((prev) => {
        const list = prev || [];
        const idx = list.findIndex((a) => a.id === incoming.id);
        if (idx === -1) return [incoming, ...list];
        const next = [...list];
        next[idx] = incoming;
        return next;
      });
    };
    socket.on('alert_update', onAlertUpdate);
    return () => socket.off('alert_update', onAlertUpdate);
  }, [socket]);

  const runTestAlert = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/alerts/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Échec du test');

      const recipients = Array.isArray(data.recipients) ? data.recipients : [];
      const telegramOk = recipients.filter((r) => r.telegram?.success).length;
      const emailOk = recipients.filter((r) => r.email?.success).length;
      toast.success(`Alerte de test créée - ${recipients.length} destinataire(s) qualifié(s), Telegram: ${telegramOk} envoyé(s), Email: ${emailOk} envoyé(s)`);
      load({ current: false });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTesting(false);
    }
  };

  const counts = useMemo(() => {
    const list = (alerts || []).filter((a) => showTest || !a.test);
    return {
      active: list.filter((a) => a.status === 'active').length,
      critical: list.filter((a) => a.status === 'active' && a.severity === 'CRITICAL').length,
      warning: list.filter((a) => a.status === 'active' && a.severity === 'WARNING').length,
      resolved: list.filter((a) => a.status === 'resolved').length
    };
  }, [alerts, showTest]);

  const filtered = useMemo(() => {
    let list = alerts || [];
    if (!showTest) list = list.filter((a) => !a.test);
    if (plantFilter !== 'all') list = list.filter((a) => a.plant_id === plantFilter);
    if (severityFilter !== 'all') list = list.filter((a) => a.severity === severityFilter);
    if (statusFilter !== 'all') list = list.filter((a) => a.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((a) =>
        (a.panel_id || '').toLowerCase().includes(q) ||
        (a.station_id || '').toLowerCase().includes(q) ||
        (a.title || '').toLowerCase().includes(q) ||
        (a.message || '').toLowerCase().includes(q)
      );
    }
    // Active alerts are visually/operationally more important - grouped
    // first regardless of the backend's newest-first ordering, then by
    // recency within each group.
    return [...list].sort((a, b) => {
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      return new Date(b.triggeredAt) - new Date(a.triggeredAt);
    });
  }, [alerts, showTest, plantFilter, severityFilter, statusFilter, search]);

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Alertes' }]} />
      <div className="page-header">
        <h1><Bell size={22} /> Alertes</h1>
        {user?.role === 'admin' && (
          <button className="btn-secondary" onClick={runTestAlert} disabled={testing}>
            <FlaskConical size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {testing ? 'Envoi...' : 'Créer une alerte de test'}
          </button>
        )}
      </div>

      {alerts === null ? (
        <LoadingState label="Chargement des alertes..." />
      ) : error && alerts.length === 0 ? (
        <ErrorState label="Impossible de charger les alertes" />
      ) : (
        <>
          <div className="alert-summary-strip">
            <div className="alert-summary-item alert-summary-active"><span className="alert-summary-value">{counts.active}</span><span className="alert-summary-label">Actives</span></div>
            <div className="alert-summary-item alert-summary-critical"><span className="alert-summary-value">{counts.critical}</span><span className="alert-summary-label">Critiques</span></div>
            <div className="alert-summary-item alert-summary-warning"><span className="alert-summary-value">{counts.warning}</span><span className="alert-summary-label">Avertissements</span></div>
            <div className="alert-summary-item alert-summary-resolved"><span className="alert-summary-value">{counts.resolved}</span><span className="alert-summary-label">Résolues</span></div>
          </div>

          <div className="alert-filter-bar">
            <select value={plantFilter} onChange={(e) => setPlantFilter(e.target.value)}>
              <option value="all">Toutes les centrales</option>
              {safePlants.map((p) => <option key={p.plant_id} value={p.plant_id}>{p.name || p.plant_id}</option>)}
            </select>
            <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
              <option value="all">Toutes sévérités</option>
              <option value="CRITICAL">Critique</option>
              <option value="WARNING">Avertissement</option>
              <option value="INFO">Information</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous statuts</option>
              <option value="active">Active</option>
              <option value="resolved">Résolue</option>
            </select>
            <div className="alert-search-box">
              <Search size={14} />
              <input type="text" placeholder="Rechercher (panneau, station, message...)" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <label className="checkbox-item alert-test-toggle">
              <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} /> Afficher les alertes de test
            </label>
          </div>

          {filtered.length === 0 ? (
            <EmptyState label="Aucune alerte ne correspond aux filtres" />
          ) : (
            <ul className="alert-card-list">
              {filtered.map((a) => (
                <li key={a.id} className={`alert-card alert-card-${(a.severity || 'info').toLowerCase()}`}>
                  <div className="alert-card-top">
                    <span className={`severity-badge severity-badge-${(a.severity || 'info').toLowerCase()}`}>{SEVERITY_LABELS[a.severity] || a.severity}</span>
                    <span className={`status-badge status-badge-${a.status}`}>{a.status === 'active' ? 'ACTIVE' : 'RÉSOLUE'}</span>
                    {a.test && <span className="test-badge">TEST</span>}
                    <span className="alert-card-plant">{plantLabel(a.plant_id)}</span>
                    {(a.panel_id || a.station_id) && <span className="alert-card-target">{a.panel_id || a.station_id}</span>}
                  </div>
                  <div className="alert-card-title">{a.title}</div>
                  <div className="alert-card-message">{a.message}</div>
                  <div className="alert-card-times">
                    <span>Déclenché : {a.triggeredAt ? new Date(a.triggeredAt).toLocaleString('fr-FR') : '—'}</span>
                    {a.resolvedAt && <span>Résolu : {new Date(a.resolvedAt).toLocaleString('fr-FR')}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
